/**
 * Subscription Service Facade
 * 
 * Este arquivo agora atua como um ponto central de exportação (Facade)
 * para os módulos refatorados em services/modules/subscription/.
 * 
 * Mantém compatibilidade com código legado que importa deste caminho.
 */

import { fetchAvailablePlans } from './subscription/subscription.api';
import { SubscriptionPlan, getPlanLabel, getPlanDescription } from '../../domain/entities/Subscription';

// Re-export API functions
export * from './subscription/subscription.api';
export * from './subscription/subscription.limits';
export * from './subscription/subscription.mapper';

// =============================================================================
// LEGACY COMPATIBILITY LAYERS
// =============================================================================
// Estas funções serão marcadas como @deprecated mas mantidas até refatoração completa dos consumidores

// Actually, the formatter is pure functions wrapped in a hook.
// I will extract the pure functions to a separate helper file se needed, 
// but for now I will duplicate the logic ou just let the consumers use the new hook.
// But wait, `getSubscriptionPlans` (legacy) used these formatters.

/**
 * Busca todos os planos de assinatura (Legado - compatibilidade)
 * Retorna no formato esperado pela UI (SubscriptionPlan do types.ts)
 * Filtra apenas planos integrados com Stripe (que têm stripe_price_id)
 * @deprecated Use fetchAvailablePlans para novo código
 */
export const getSubscriptionPlans = async () => {
    try {
        const plans = await fetchAvailablePlans();
        
        // Filtrar apenas planos que têm integração com Stripe
        const stripePlans = plans.filter(plan => plan.stripePriceId !== null);
        
        // Se nenhum plano tem stripe_price_id, mostrar planos pagos como fallback
        const plansToShow = stripePlans.length > 0 
            ? stripePlans 
            : plans.filter(plan => ['basic', 'pro', 'enterprise'].includes(plan.id));
            
        // Mapeamento para formato da UI usando labels/descrições do domínio
        const uiPlans = plansToShow.map(plan => ({
            id: plan.id,
            name: getPlanLabel(plan.id),
            price: formatPrice(plan.pricePerUser),
            description: getPlanDescription(plan.id),
            features: getPlanFeatures(plan),
            isRecommended: plan.id === 'pro',
        }));
        
        // Ordenar conforme legado
        const PLAN_ORDER = ['Básico', 'Pro', 'Enterprise'];
        return uiPlans.sort((a, b) => {
            const indexA = PLAN_ORDER.indexOf(a.name);
            const indexB = PLAN_ORDER.indexOf(b.name);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    } catch (error) {
        console.error('Error fetching plans:', error);
        return [];
    }
};

/**
 * Busca a assinatura atual do usuário (Legado - compatibilidade)
 * @deprecated Use fetchOrganizationSubscription
 */
export const getCurrentSubscription = async (organizationId?: string) => {
    // Implementação mantida para compatibilidade, importando da nova API
    const { fetchOrganizationSubscription } = await import('./subscription/subscription.api');

    const DEFAULT_SUBSCRIPTION = {
        planName: 'Trial',
        billingType: 'MENSALMENTE' as const,
        totalSeats: 1,
        usedSeats: 0,
        pricePerSeat: 0,
        isLegacy: false,
    };

    if (!organizationId) return DEFAULT_SUBSCRIPTION;

    try {
        const subscription = await fetchOrganizationSubscription(organizationId);
        if (!subscription) return DEFAULT_SUBSCRIPTION;

        return {
            planName: subscription.planId,
            billingType: 'MENSALMENTE' as const,
            totalSeats: subscription.quantity,
            usedSeats: 0,
            pricePerSeat: 0,
            isLegacy: false,
        };
    } catch (error) {
        console.error('Error fetching subscription:', error);
        return DEFAULT_SUBSCRIPTION;
    }
};

function formatPrice(price: number | string): string {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '0,00';
    return numPrice.toFixed(2).replace('.', ',');
}

function getPlanFeatures(plan: SubscriptionPlan): string[] {
    if (plan.features.displayItems && plan.features.displayItems.length > 0) {
        return plan.features.displayItems;
    }
    return [];
}
